import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { createSnapModifier } from '@dnd-kit/modifiers'
import {
  ClipboardCheck,
  Grid3X3,
  Info,
  MapPin,
  Mic2,
  RotateCcw,
  Settings2,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { domAnimation, LazyMotion, m } from 'motion/react'
import { memo, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { TableMasterResponse } from '@/lib/api/client/types.gen'

interface TableLayoutEditorProps {
  tables: TableMasterResponse[]
  tableOccupancyMap?: Record<string, number>
  onUpdatePosition: (
    id: string,
    x: number,
    y: number,
    extra?: Partial<TableMasterResponse>,
  ) => Promise<void>
  isLoading?: boolean
}

interface DraggableTableProps {
  table: TableMasterResponse
  occupancy: number
  scale: number
  isSelected: boolean
  onSelect: () => void
  onResize: (
    id: string,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => void
}

const getPointStyles = (table: TableMasterResponse, scale: number) => {
  const type = table.type || 'table'
  const shape = table.shape || 'circle'
  const customWidth =
    table.width || (type === 'stage' ? 120 : type === 'registration' ? 100 : 60)
  const customHeight =
    table.height || (type === 'stage' ? 80 : type === 'registration' ? 60 : 60)

  let borderRadius = '0px'
  if (shape === 'circle') borderRadius = '9999px'
  else if (shape === 'square' || shape === 'rectangle') borderRadius = '12px'

  switch (type) {
    case 'stage':
      return {
        className: 'border-orange-500 bg-orange-50 text-orange-700',
        width: customWidth,
        height: customHeight,
        borderRadius,
        icon: <Mic2 className="size-4" />,
        label: 'STAGE',
      }
    case 'registration':
      return {
        className: 'border-blue-500 bg-blue-50 text-blue-700',
        width: customWidth,
        height: customHeight,
        borderRadius,
        icon: <ClipboardCheck className="size-4" />,
        label: 'REG',
      }
    case 'other':
      return {
        className: 'border-gray-400 bg-gray-100 text-gray-600',
        width: customWidth,
        height: customHeight,
        borderRadius,
        icon: <MapPin className="size-4" />,
        label: '',
      }
    default:
      return {
        className:
          'border-white bg-white/90 text-foreground shadow-lg backdrop-blur-sm',
        width: customWidth,
        height: customHeight,
        borderRadius,
        icon: null,
        label: '',
      }
  }
}

const DraggableTable = memo(
  ({
    table,
    occupancy,
    scale,
    isSelected,
    onSelect,
    onResize,
  }: DraggableTableProps) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } =
      useDraggable({
        id: table.id,
        disabled: false,
      })

    const [localSize, setLocalSize] = useState({
      width: table.width || 60,
      height: table.height || 60,
    })
    const [localPos, setLocalPos] = useState({
      x: table.x || 0,
      y: table.y || 0,
    })
    const isResizing = useRef(false)
    const startPos = useRef({ x: 0, y: 0 })
    const startSize = useRef({ width: 0, height: 0 })
    const startCoord = useRef({ x: 0, y: 0 })

    const currentSize = useRef({ width: 0, height: 0 })
    const currentCoord = useRef({ x: 0, y: 0 })

    useEffect(() => {
      if (!isResizing.current) {
        const initialSize = {
          width: table.width || 60,
          height: table.height || 60,
        }
        setLocalSize(initialSize)
        setLocalPos({ x: table.x || 0, y: table.y || 0 })
        currentSize.current = initialSize
        currentCoord.current = { x: table.x || 0, y: table.y || 0 }
      }
    }, [table.width, table.height, table.x, table.y])

    const handleResizeStart = (e: React.PointerEvent, handle: string) => {
      e.stopPropagation()
      e.preventDefault()
      isResizing.current = true
      startPos.current = { x: e.clientX, y: e.clientY }
      startSize.current = { ...localSize }
      startCoord.current = { ...localPos }

      const handlePointerMove = (moveEvent: PointerEvent) => {
        if (!isResizing.current) return
        const dx = (moveEvent.clientX - startPos.current.x) / scale
        const dy = (moveEvent.clientY - startPos.current.y) / scale

        let newWidth = startSize.current.width
        let newHeight = startSize.current.height
        let newX = startCoord.current.x
        let newY = startCoord.current.y

        if (handle.includes('e')) {
          newWidth = Math.max(40, startSize.current.width + dx)
        }
        if (handle.includes('w')) {
          const delta = Math.min(startSize.current.width - 40, dx)
          newWidth = startSize.current.width - delta
          newX = startCoord.current.x + delta
        }
        if (handle.includes('s')) {
          newHeight = Math.max(40, startSize.current.height + dy)
        }
        if (handle.includes('n')) {
          const delta = Math.min(startSize.current.height - 40, dy)
          newHeight = startSize.current.height - delta
          newY = startCoord.current.y + delta
        }

        setLocalSize({ width: newWidth, height: newHeight })
        setLocalPos({ x: newX, y: newY })
        currentSize.current = { width: newWidth, height: newHeight }
        currentCoord.current = { x: newX, y: newY }
      }

      const handlePointerUp = () => {
        if (isResizing.current) {
          isResizing.current = false
          onResize(
            table.id,
            Math.round(currentCoord.current.x),
            Math.round(currentCoord.current.y),
            Math.round(currentSize.current.width),
            Math.round(currentSize.current.height),
          )
        }
        window.removeEventListener('pointermove', handlePointerMove)
        window.removeEventListener('pointerup', handlePointerUp)
      }

      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)
    }

    const style = {
      transform: transform
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
      left: `${localPos.x}px`,
      top: `${localPos.y}px`,
      position: 'absolute' as const,
      zIndex: isDragging ? 100 : 1,
      touchAction: 'none',
    }

    const pointStyles = getPointStyles(table, scale)

    return (
      <LazyMotion features={domAnimation}>
        <div
          ref={setNodeRef}
          style={style}
          {...listeners}
          {...attributes}
          role="button"
          tabIndex={0}
          className={`group cursor-grab active:cursor-grabbing`}
          data-draggable="true"
          onClick={(e) => {
            e.stopPropagation()
            onSelect()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation()
              onSelect()
            }
          }}
        >
          <m.div
            whileHover={isSelected || isDragging ? undefined : { scale: 1.05 }}
            whileTap={isSelected || isDragging ? undefined : { scale: 0.95 }}
            className={`flex flex-col items-center justify-center border-2 ${
              pointStyles.className
            } ${
              isSelected
                ? 'ring-primary border-primary shadow-xl ring-2 ring-offset-2'
                : isDragging
                  ? 'ring-primary scale-105 shadow-xl ring-2 ring-offset-2'
                  : ''
            }`}
            style={{
              width: `${localSize.width}px`,
              height: `${localSize.height}px`,
              borderRadius: pointStyles.borderRadius,
            }}
          >
            {pointStyles.icon && (
              <div className="mb-1 opacity-50">{pointStyles.icon}</div>
            )}
            <span
              className="leading-none font-bold"
              style={{ fontSize: `14px` }}
            >
              {table.tableNumber}
            </span>
            {table.type === 'table' && scale > 0.7 && (
              <span className="text-muted-foreground mt-0.5 text-[10px] leading-none font-medium">
                {occupancy}/{table.capacity}
              </span>
            )}
            {pointStyles.label && (
              <span className="mt-1 text-[8px] font-bold tracking-widest opacity-30">
                {pointStyles.label}
              </span>
            )}

            {!isSelected && (
              <div className="absolute top-1 right-1 opacity-0 transition-opacity group-hover:opacity-100">
                <div className="bg-primary flex size-5 items-center justify-center rounded-full text-white shadow-sm">
                  <Settings2 className="size-3" />
                </div>
              </div>
            )}

            {isSelected && (
              <>
                {/* Corner handles */}
                <div
                  className="absolute -top-1 -left-1 z-60 size-3 cursor-nwse-resize rounded-full border bg-white shadow-sm"
                  onPointerDown={(e) => handleResizeStart(e, 'nw')}
                />
                <div
                  className="absolute -top-1 -right-1 z-60 size-3 cursor-nesw-resize rounded-full border bg-white shadow-sm"
                  onPointerDown={(e) => handleResizeStart(e, 'ne')}
                />
                <div
                  className="absolute -bottom-1 -left-1 z-60 size-3 cursor-nesw-resize rounded-full border bg-white shadow-sm"
                  onPointerDown={(e) => handleResizeStart(e, 'sw')}
                />
                <div
                  className="absolute -right-2 -bottom-2 z-60 flex size-6 cursor-nwse-resize items-center justify-center rounded-full border bg-white shadow-lg hover:scale-110 active:scale-95"
                  onPointerDown={(e) => handleResizeStart(e, 'se')}
                >
                  <div className="bg-primary size-2 rounded-sm" />
                </div>

                {/* Edge handles - Only for square/rectangle */}
                {table.shape !== 'circle' && (
                  <>
                    <div
                      className="bg-primary/20 hover:bg-primary/50 absolute top-0 left-1/2 z-60 h-1 w-8 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize rounded-full"
                      onPointerDown={(e) => handleResizeStart(e, 'n')}
                    />
                    <div
                      className="bg-primary/20 hover:bg-primary/50 absolute bottom-0 left-1/2 z-60 h-1 w-8 -translate-x-1/2 translate-y-1/2 cursor-ns-resize rounded-full"
                      onPointerDown={(e) => handleResizeStart(e, 's')}
                    />
                    <div
                      className="bg-primary/20 hover:bg-primary/50 absolute top-1/2 left-0 z-60 h-8 w-1 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize rounded-full"
                      onPointerDown={(e) => handleResizeStart(e, 'w')}
                    />
                    <div
                      className="bg-primary/20 hover:bg-primary/50 absolute top-1/2 right-0 z-60 h-8 w-1 translate-x-1/2 -translate-y-1/2 cursor-ew-resize rounded-full"
                      onPointerDown={(e) => handleResizeStart(e, 'e')}
                    />
                  </>
                )}
              </>
            )}
          </m.div>
        </div>
      </LazyMotion>
    )
  },
)

export const TableLayoutEditor = ({
  tables,
  tableOccupancyMap = {},
  onUpdatePosition,
  isLoading,
}: TableLayoutEditorProps) => {
  const [scale, setScale] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [snapToGrid, setSnapToGrid] = useState(true)
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null)

  // Local state for panel inputs to avoid lag
  const [panelSize, setPanelSize] = useState({ width: 0, height: 0 })

  const containerRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const dragStartPan = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const handleWheelNative = (e: WheelEvent) => {
      e.preventDefault()
      if (e.ctrlKey || e.metaKey) {
        // Zoom
        const zoomSpeed = 0.001
        const delta = -e.deltaY * zoomSpeed
        setScale((prev) => Math.min(Math.max(0.2, prev + delta), 3))
      }
    }

    viewport.addEventListener('wheel', handleWheelNative, { passive: false })
    return () => viewport.removeEventListener('wheel', handleWheelNative)
  }, [])

  const gridStep = 20
  const snapModifier = createSnapModifier(gridStep)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  const stopAutoPan = () => {}

  const handleDragStart = () => {
    dragStartPan.current = { ...pan }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    stopAutoPan()
    const { active, delta } = event
    const tableId = active.id as string
    const table = tables.find((t) => t.id === tableId)

    if (table) {
      // Account for scale and pan shift during drag
      const panShiftX = dragStartPan.current.x - pan.x
      const panShiftY = dragStartPan.current.y - pan.y

      let newX = (table.x || 0) + (delta.x + panShiftX) / scale
      let newY = (table.y || 0) + (delta.y + panShiftY) / scale

      if (snapToGrid) {
        newX = Math.round(newX / gridStep) * gridStep
        newY = Math.round(newY / gridStep) * gridStep
      } else {
        newX = Math.round(newX)
        newY = Math.round(newY)
      }

      // Prevent negative coordinates
      newX = Math.max(0, newX)
      newY = Math.max(0, newY)

      setIsSaving(true)
      toast.info(`กำลังบันทึกตำแหน่ง: ${newX}, ${newY}`)
      try {
        await onUpdatePosition(tableId, newX, newY, {
          shape: table.shape,
          type: table.type,
          width: table.width,
          height: table.height,
        })
      } finally {
        setIsSaving(false)
      }
    }
  }

  const selectedTable = tables.find((t) => t.id === selectedTableId)

  // Update panel size when selection changes
  useEffect(() => {
    if (selectedTable) {
      setPanelSize({
        width: selectedTable.width || 60,
        height: selectedTable.height || 60,
      })
    }
  }, [selectedTableId, selectedTable?.width, selectedTable?.height])

  // Debounced save for panel inputs
  useEffect(() => {
    if (!selectedTable) return

    const hasChanged =
      panelSize.width !== (selectedTable.width || 60) ||
      panelSize.height !== (selectedTable.height || 60)

    if (!hasChanged) return

    const timer = setTimeout(() => {
      onUpdatePosition(
        selectedTable.id,
        selectedTable.x || 0,
        selectedTable.y || 0,
        {
          width: panelSize.width,
          height: panelSize.height,
          type: selectedTable.type,
          shape: selectedTable.shape,
        },
      )
    }, 500)

    return () => clearTimeout(timer)
  }, [panelSize, selectedTable, onUpdatePosition])

  const handleZoomIn = () => setScale((s) => Math.min(s + 0.1, 2))
  const handleZoomOut = () => setScale((s) => Math.max(s - 0.1, 0.5))
  const handleReset = () => {
    setScale(1)
    setPan({ x: 0, y: 0 })
  }

  // Panning Logic
  const handlePanStart = (e: React.PointerEvent) => {
    // Pan on left-click background OR middle-click anywhere
    const isMiddleClick = e.button === 1
    // Check if we're clicking a table or its children
    const isTableClick = (e.target as HTMLElement).closest(
      '[data-draggable="true"]',
    )

    if (!isMiddleClick && isTableClick) return

    e.preventDefault()
    setIsPanning(true)
    const startPan = { ...pan }
    const startPos = { x: e.clientX, y: e.clientY }

    let lastPos = { x: e.clientX, y: e.clientY }
    let velocity = { x: 0, y: 0 }

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - startPos.x
      const dy = moveEvent.clientY - startPos.y

      // Calculate velocity for momentum
      velocity = {
        x: moveEvent.clientX - lastPos.x,
        y: moveEvent.clientY - lastPos.y,
      }
      lastPos = { x: moveEvent.clientX, y: moveEvent.clientY }

      setPan({
        x: startPan.x + dx,
        y: startPan.y + dy,
      })
    }

    const handlePointerUp = () => {
      setIsPanning(false)
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)

      // Optional: Add simple inertia here if desired
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
  }

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="border-primary size-8 animate-spin rounded-full border-3 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-250px)] flex-col gap-4 overflow-hidden rounded-3xl border bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-bold">แผนผังที่นั่ง</h2>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Info className="size-3" />
            ลากและวางเพื่อเปลี่ยนตำแหน่งโต๊ะ
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={snapToGrid ? 'default' : 'outline'}
            size="sm"
            className="h-9 gap-2 rounded-xl"
            onClick={() => setSnapToGrid(!snapToGrid)}
          >
            <Grid3X3 className="size-4" />
            <span className="hidden sm:inline">จัดแนวตาราง</span>
          </Button>
          <div className="flex items-center rounded-lg border bg-gray-50 p-1">
            <Button
              variant="ghost"
              size="icon"
              className="size-8 rounded-md"
              onClick={handleZoomOut}
            >
              <ZoomOut className="size-4" />
            </Button>
            <div className="w-12 text-center text-xs font-bold">
              {Math.round(scale * 100)}%
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 rounded-md"
              onClick={handleZoomIn}
            >
              <ZoomIn className="size-4" />
            </Button>
          </div>
          <Button
            variant="outline"
            size="icon"
            className="size-10 rounded-xl"
            onClick={handleReset}
          >
            <RotateCcw className="size-4" />
          </Button>
          <div
            className={`flex items-center gap-2 px-3 text-xs font-medium transition-opacity ${isSaving ? 'opacity-100' : 'opacity-0'}`}
          >
            <div className="border-primary size-3 animate-spin rounded-full border-2 border-t-transparent" />
            กำลังบันทึก...
          </div>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden">
        <div
          ref={viewportRef}
          role="button"
          tabIndex={0}
          className={`h-full rounded-2xl bg-gray-50/50 outline-none ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`}
          onPointerDown={handlePanStart}
          onClick={() => setSelectedTableId(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setSelectedTableId(null)
          }}
        >
          {/* Infinite Canvas Container */}
          <div
            ref={containerRef}
            className="h-full w-full"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
              transformOrigin: '0 0',
              transition: isPanning ? 'none' : 'transform 0.1s ease-out',
            }}
          >
            {/* Grid Background (Full Canvas) */}
            <div
              className="pointer-events-none absolute -inset-2500"
              style={{
                backgroundImage:
                  'radial-gradient(#e5e7eb 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            />

            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              modifiers={[
                // restrictToFirstScrollableAncestor, // Disable this for infinite canvas
                ...(snapToGrid ? [snapModifier] : []),
              ]}
            >
              {tables.map((table) => (
                <DraggableTable
                  key={table.id}
                  table={table}
                  occupancy={tableOccupancyMap[table.id] || 0}
                  scale={scale}
                  isSelected={selectedTableId === table.id}
                  onSelect={() => setSelectedTableId(table.id)}
                  onResize={(id, x, y, w, h) =>
                    onUpdatePosition(id, x, y, {
                      width: w,
                      height: h,
                      shape: table.shape,
                      type: table.type,
                    })
                  }
                />
              ))}
            </DndContext>
          </div>
        </div>

        {/* Properties Panel Overlay */}
        {selectedTable && (
          <div
            className="absolute top-4 right-4 z-50 w-64 rounded-2xl border bg-white/95 p-4 shadow-2xl backdrop-blur-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-xs font-bold tracking-wider text-gray-400 uppercase">
                  กำลังแก้ไข
                </span>
                <h3 className="text-sm font-bold">
                  โต๊ะ {selectedTable.tableNumber}
                </h3>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:text-primary size-8 rounded-full text-gray-400"
                  onClick={() => {
                    const type = selectedTable.type
                    const defaultWidth =
                      type === 'stage'
                        ? 120
                        : type === 'registration'
                          ? 100
                          : 60
                    const defaultHeight =
                      type === 'stage' ? 80 : type === 'registration' ? 60 : 60
                    const defaultShape =
                      type === 'stage' || type === 'registration'
                        ? 'rectangle'
                        : 'circle'

                    toast.info('คืนค่าขนาดและรูปทรงเริ่มต้น')
                    onUpdatePosition(
                      selectedTable.id,
                      selectedTable.x || 0,
                      selectedTable.y || 0,
                      {
                        width: defaultWidth,
                        height: defaultHeight,
                        type: selectedTable.type,
                        shape: defaultShape,
                      },
                    )
                  }}
                  title="รีเซ็ตขนาดและรูปทรง"
                >
                  <RotateCcw className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 rounded-full"
                  onClick={() => setSelectedTableId(null)}
                >
                  <X className="size-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-500 uppercase">
                  รูปทรง
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['circle', 'rectangle'].map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() =>
                        onUpdatePosition(
                          selectedTable.id,
                          selectedTable.x || 0,
                          selectedTable.y || 0,
                          {
                            shape: s as 'circle' | 'rectangle',
                            type: selectedTable.type,
                            width: selectedTable.width,
                            height: selectedTable.height,
                          },
                        )
                      }
                      className={`flex h-8 items-center justify-center rounded-lg border text-[10px] font-bold transition-all ${
                        selectedTable.shape === s ||
                        (s === 'rectangle' && selectedTable.shape === 'square')
                          ? 'border-primary bg-primary text-white'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      {s === 'circle' ? 'วงกลม' : 'สี่เหลี่ยม'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-500 uppercase">
                    กว้าง (px)
                  </label>
                  <Input
                    type="number"
                    value={panelSize.width}
                    onChange={(e) =>
                      setPanelSize((prev) => ({
                        ...prev,
                        width: Number(e.target.value) || 0,
                      }))
                    }
                    className="focus:ring-primary h-8 w-full rounded-lg border bg-gray-50 px-2 py-1.5 text-xs font-bold focus:ring-2 focus:outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-500 uppercase">
                    สูง (px)
                  </label>
                  <Input
                    type="number"
                    value={panelSize.height}
                    onChange={(e) =>
                      setPanelSize((prev) => ({
                        ...prev,
                        height: Number(e.target.value) || 0,
                      }))
                    }
                    className="focus:ring-primary h-8 w-full rounded-lg border bg-gray-50 px-2 py-1.5 text-xs font-bold focus:ring-2 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-full border border-gray-300 bg-white" />
            <span className="text-xs text-gray-500">โต๊ะอาหาร</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-md border border-orange-500 bg-orange-50" />
            <span className="text-xs text-gray-500">เวที</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-md border border-blue-500 bg-blue-50" />
            <span className="text-xs text-gray-500">ลงทะเบียน</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-3 rounded-md border border-gray-400 bg-gray-100" />
            <span className="text-xs text-gray-500">อื่นๆ</span>
          </div>
        </div>
        <p className="text-xs text-gray-400">
          * ตำแหน่งจะถูกบันทึกอัตโนมัติเมื่อวางโต๊ะ
        </p>
      </div>
    </div>
  )
}
